/**
 * Slide Loading Optimizer
 * 
 * Optimizes slide loading with lazy loading, batching, and prefetching strategies
 * Replaces eager loading of all slides with viewport-based loading
 */

import { filesystemAPI } from './api';
import { logger } from '../utils/logger';

interface SlideInfo {
  scene_id: string;
  title: string;
  layout_type: string;
  content_description?: string;
  script_text?: string;
  estimated_duration?: number;
  [key: string]: any;
}

interface Slide {
  id: string;
  path: string;
  source: 'html' | 'plan';
  [key: string]: any;
}

interface LoadOptions {
  batchSize?: number;
  prefetchAdjacent?: number;
  useCache?: boolean;
}

export class SlideLoadingOptimizer {
  private static instance: SlideLoadingOptimizer;
  private loadedSlides: Map<string, SlideInfo> = new Map();
  private loadingPromises: Map<string, Promise<SlideInfo | null>> = new Map();
  private readonly CACHE_PREFIX = 'slide_info_';

  constructor() {
    if (SlideLoadingOptimizer.instance) {
      return SlideLoadingOptimizer.instance;
    }
    SlideLoadingOptimizer.instance = this;
  }

  /**
   * Load slide info lazily - only when needed
   */
  async loadSlideInfo(
    slidePath: string,
    conversationId?: string,
    options: LoadOptions = {}
  ): Promise<SlideInfo | null> {
    const { useCache = true } = options;
    const cacheKey = this.getCacheKey(slidePath, conversationId);

    // Check memory cache first
    if (useCache && this.loadedSlides.has(cacheKey)) {
      return this.loadedSlides.get(cacheKey)!;
    }

    // Check if already loading
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey)!;
    }

    // Check session storage cache
    if (useCache) {
      const cached = this.getFromSessionStorage(cacheKey);
      if (cached) {
        this.loadedSlides.set(cacheKey, cached);
        return cached;
      }
    }

    // Start loading
    const loadPromise = this.fetchSlideInfo(slidePath);
    this.loadingPromises.set(cacheKey, loadPromise);

    try {
      const info = await loadPromise;
      if (info) {
        this.loadedSlides.set(cacheKey, info);
        this.saveToSessionStorage(cacheKey, info);
      }
      return info;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  /**
   * Fetch slide info from filesystem
   */
  private async fetchSlideInfo(slidePath: string): Promise<SlideInfo | null> {
    try {
      const slideDir = slidePath.substring(0, slidePath.lastIndexOf('/'));
      const infoPath = `${slideDir}/info.json`;
      
      const response = await filesystemAPI.read(infoPath);
      const info = JSON.parse(response.content);
      
      return info;
    } catch (error) {
      logger.debug(`Could not load info for ${slidePath}:`, error);
      return null;
    }
  }

  /**
   * Load multiple slides in batches to avoid overwhelming the API
   */
  async loadSlidesBatch(
    slides: Slide[],
    conversationId?: string,
    options: LoadOptions = {}
  ): Promise<SlideInfo[]> {
    const { batchSize = 5 } = options;
    const results: SlideInfo[] = [];

    // Filter only HTML slides that need info
    const htmlSlides = slides.filter(slide => slide.source === 'html' && slide.path);

    // Process in batches
    for (let i = 0; i < htmlSlides.length; i += batchSize) {
      const batch = htmlSlides.slice(i, i + batchSize);
      const batchPromises = batch.map(slide =>
        this.loadSlideInfo(slide.path, conversationId, options)
          .then(info => info || undefined)
          .catch(() => undefined)
      );

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults.filter((info): info is SlideInfo => info !== undefined));
    }

    return results;
  }

  /**
   * Load only visible slides and optionally prefetch adjacent ones
   */
  async loadVisibleSlides(
    slides: Slide[],
    visibleIndices: number[],
    conversationId?: string,
    options: LoadOptions = {}
  ): Promise<SlideInfo[]> {
    const { prefetchAdjacent = 1 } = options;
    const indicesToLoad = new Set<number>();

    // Add visible indices
    visibleIndices.forEach(index => {
      indicesToLoad.add(index);
    });

    // Add adjacent indices for prefetching
    if (prefetchAdjacent > 0) {
      visibleIndices.forEach(index => {
        for (let i = 1; i <= prefetchAdjacent; i++) {
          if (index - i >= 0) indicesToLoad.add(index - i);
          if (index + i < slides.length) indicesToLoad.add(index + i);
        }
      });
    }

    // Load selected slides
    const slidesToLoad = Array.from(indicesToLoad)
      .sort((a, b) => a - b)
      .map(index => slides[index])
      .filter(slide => slide && slide.source === 'html');

    return this.loadSlidesBatch(slidesToLoad, conversationId, options);
  }

  /**
   * Prefetch slide info in background (fire and forget)
   */
  prefetchSlide(slidePath: string, conversationId?: string): void {
    // Don't await - let it run in background
    this.loadSlideInfo(slidePath, conversationId).catch(error => {
      logger.debug(`Prefetch failed for ${slidePath}:`, error);
    });
  }

  /**
   * Clear all cached slide info
   */
  clearCache(conversationId?: string): void {
    if (conversationId) {
      // Clear specific conversation
      const keysToDelete: string[] = [];
      this.loadedSlides.forEach((_, key) => {
        if (key.includes(conversationId)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.loadedSlides.delete(key));

      // Clear from session storage
      this.clearSessionStorage(conversationId);
    } else {
      // Clear all
      this.loadedSlides.clear();
      this.clearSessionStorage();
    }
  }

  /**
   * Clear cache for a specific slide
   */
  clearSlideCache(slidePath: string, conversationId?: string): void {
    const cacheKey = this.getCacheKey(slidePath, conversationId);
    
    // Clear from memory cache
    this.loadedSlides.delete(cacheKey);
    this.loadingPromises.delete(cacheKey);
    
    // Clear from session storage
    try {
      sessionStorage.removeItem(cacheKey);
    } catch (error) {
      logger.debug('Failed to clear slide from session storage:', error);
    }
    
    logger.debug(`Cleared cache for slide: ${slidePath}`);
  }

  /**
   * Get count of loaded slides
   */
  getLoadedCount(): number {
    return this.loadedSlides.size;
  }

  /**
   * Get cache key for a slide
   */
  private getCacheKey(slidePath: string, conversationId?: string): string {
    const parts = [this.CACHE_PREFIX];
    if (conversationId) {
      parts.push(conversationId);
    }
    parts.push(slidePath.replace(/[^a-zA-Z0-9]/g, '_'));
    return parts.join('');
  }

  /**
   * Get from session storage
   */
  private getFromSessionStorage(key: string): SlideInfo | null {
    try {
      const cached = sessionStorage.getItem(key);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      logger.debug('Session storage read error:', error);
    }
    return null;
  }

  /**
   * Save to session storage
   */
  private saveToSessionStorage(key: string, info: SlideInfo): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(info));
    } catch (error) {
      logger.debug('Session storage write error:', error);
      // Ignore quota errors
    }
  }

  /**
   * Clear session storage
   */
  private clearSessionStorage(conversationId?: string): void {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(this.CACHE_PREFIX)) {
        if (!conversationId || key.includes(conversationId)) {
          sessionStorage.removeItem(key);
        }
      }
    });
  }

  /**
   * Static helper to get singleton instance
   */
  static getInstance(): SlideLoadingOptimizer {
    if (!SlideLoadingOptimizer.instance) {
      SlideLoadingOptimizer.instance = new SlideLoadingOptimizer();
    }
    return SlideLoadingOptimizer.instance;
  }
}

// Export singleton instance
export const slideLoadingOptimizer = SlideLoadingOptimizer.getInstance();